// CELL-BIO9 — save slot selection screen.
// Shown at boot before the game starts. Renders into #terminal using the
// existing Terminal class so it inherits CRT + typewriter for free.
// Returns the chosen slot number and whether it was a fresh start.

import type { SlotNumber } from "../engine/saveManager";
import { loadSlot, clearSlot } from "../engine/saveManager";
import { audioEngine } from "../engine/audio";

interface SlotChoice {
  slot: SlotNumber;
  fresh: boolean;
}

function slotSummary(slot: SlotNumber): string {
  const save = loadSlot(slot);
  if (!save) return "[EMPTY]";
  const { rep, intel, muscle, heat } = save.stats;
  return `DAY ${save.day}  REP ${String(rep).padStart(2, "0")}  INTEL ${String(intel).padStart(2, "0")}  MUSCLE ${String(muscle).padStart(2, "0")}  HEAT ${String(heat).padStart(2, "0")}`;
}

/** Render the slot select screen and resolve with the player's choice. */
export function showSlotSelect(
  terminal: HTMLElement,
  choices: HTMLElement
): Promise<SlotChoice> {
  return new Promise((resolve) => {
    // Clear any existing content.
    terminal.textContent = "";
    choices.textContent = "";

    const addLine = (text: string, cls: string): void => {
      const p = document.createElement("p");
      p.className = `line ${cls}`;
      p.textContent = text;
      terminal.appendChild(p);
    };

    addLine("BLACKREACH PENITENTIARY // TERMINAL LINK ESTABLISHED", "system");
    addLine("CELL-BIO9 // CHAPTER 1: THE CELL", "system");
    addLine("", "muted");
    addLine("SELECT SAVE SLOT", "system");
    addLine("─────────────────────────────────────────", "muted");

    const slots: SlotNumber[] = [1, 2, 3];
    for (const slot of slots) {
      addLine(`  [${slot}] SLOT ${slot}  —  ${slotSummary(slot)}`, "story");
    }

    addLine("─────────────────────────────────────────", "muted");
    addLine("Press 1 / 2 / 3 to select a slot.", "muted");

    // Render slot buttons.
    const renderButtons = (confirmSlot: SlotNumber | null): void => {
      choices.textContent = "";

      if (confirmSlot !== null) {
        // Confirm overwrite.
        addLine("", "muted");
        addLine(`SLOT ${confirmSlot} has a save. Start new game? This cannot be undone.`, "warning");

        const btnYes = makeBtn("[Y] YES — ERASE AND START NEW", "warning");
        btnYes.addEventListener("click", () => {
          audioEngine.choiceBeep(0);
          clearSlot(confirmSlot);
          cleanup();
          resolve({ slot: confirmSlot, fresh: true });
        });

        const btnNo = makeBtn("[N] NO — GO BACK", "story");
        btnNo.addEventListener("click", () => {
          audioEngine.choiceBeep(1);
          renderButtons(null);
        });

        choices.appendChild(btnYes);
        choices.appendChild(btnNo);

        const onKey = (ev: KeyboardEvent): void => {
          if (ev.key === "y" || ev.key === "Y") {
            window.removeEventListener("keydown", onKey);
            audioEngine.choiceBeep(0);
            clearSlot(confirmSlot);
            cleanup();
            resolve({ slot: confirmSlot, fresh: true });
          } else if (ev.key === "n" || ev.key === "N") {
            window.removeEventListener("keydown", onKey);
            audioEngine.choiceBeep(1);
            renderButtons(null);
          }
        };
        window.addEventListener("keydown", onKey);
        return;
      }

      // Normal slot buttons.
      for (const slot of slots) {
        const hasSave = loadSlot(slot) !== null;
        const label = hasSave
          ? `[${slot}] SLOT ${slot} — CONTINUE`
          : `[${slot}] SLOT ${slot} — NEW GAME`;
        const btn = makeBtn(label, "story");
        btn.addEventListener("click", () => {
          audioEngine.choiceBeep(slot - 1);
          if (hasSave) {
            cleanup();
            resolve({ slot, fresh: false });
          } else {
            cleanup();
            resolve({ slot, fresh: true });
          }
        });
        choices.appendChild(btn);
      }

      const onKey = (ev: KeyboardEvent): void => {
        const n = Number(ev.key) as SlotNumber;
        if (n !== 1 && n !== 2 && n !== 3) return;
        window.removeEventListener("keydown", onKey);
        audioEngine.choiceBeep(n - 1);
        const hasSave = loadSlot(n) !== null;
        if (hasSave) {
          choices.textContent = "";
          resolve({ slot: n, fresh: false });
        } else {
          choices.textContent = "";
          resolve({ slot: n, fresh: true });
        }
        cleanup();
      };
      window.addEventListener("keydown", onKey);
      keyHandlerRef = onKey;
    };

    let keyHandlerRef: ((ev: KeyboardEvent) => void) | null = null;

    const cleanup = (): void => {
      if (keyHandlerRef) {
        window.removeEventListener("keydown", keyHandlerRef);
        keyHandlerRef = null;
      }
      choices.textContent = "";
    };

    renderButtons(null);
  });
}

function makeBtn(label: string, cls: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `choice ${cls}`;
  btn.textContent = label;
  return btn;
}
